import type { ISchedule } from "@gettactic/api";
import {
  getDateFromSchedule,
  getTimeFromSchedule
} from "@gettactic/helpers/src/date-time/getTimeFromSchedule";
import { isValidTime } from "@gettactic/helpers/src/date-time/isValidTime";
import { time12To24 } from "@gettactic/helpers/src/date-time/timeStringConverters";
import { format } from "date-fns";

/**
 * A resource's day as the reservation field shows it: the bookings already on the
 * resource, the window the requester is asking for, and whether the two collide.
 *
 * Everything is in minutes from the office's midnight. Schedules come in as
 * office wall-clock time with a decorative "Z" (see `getTimeFromSchedule`),
 * and the form's times are the office's too, so no zone conversion is needed
 * to compare them — only to keep them apart from the device's clock.
 */

export type MinuteRange = {
  startMinutes: number;
  endMinutes: number;
};

export type ResourceBusyBlock = MinuteRange & {
  id: string;
  /** `null` when the booking is private: the block still blocks, unnamed. */
  title: string | null;
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** Working hours the bar shows by default: 7:00 AM to 7:00 PM. */
export const DEFAULT_RESOURCE_DAY_WINDOW: MinuteRange = {
  startMinutes: 7 * MINUTES_PER_HOUR,
  endMinutes: 19 * MINUTES_PER_HOUR
};

/** Placeholder id core puts on an occupant it will not name. */
const PRIVATE_USER_ID = "Private";

/** "09:00 AM" → 540; `null` for anything the time picker would not produce. */
export const timeToMinutes = (time12: string): number | null => {
  if (!isValidTime(time12, true)) return null;
  const [hours, minutes] = time12To24(time12).split(":").map(Number);
  return hours * MINUTES_PER_HOUR + minutes;
};

/** 540 → "9:00 AM", the form the rest of the request UI uses for times. */
export const minutesToTimeLabel = (minutes: number): string => {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR) % 24;
  const rest = minutes % MINUTES_PER_HOUR;
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(rest).padStart(2, "0")} ${period}`;
};

/** 540 → "09:00 AM", the padded form the time picker holds as its value. */
export const minutesToPickerTime = (minutes: number): string => {
  const [time, period] = minutesToTimeLabel(minutes).split(" ");
  return `${time.padStart(5, "0")} ${period}`;
};

/** Clicks land on the half hour: fine enough to aim, coarse enough to hit. */
const SNAP_MINUTES = 30;
/** What a click books when no window is picked yet. */
const DEFAULT_DURATION_MINUTES = 60;
/**
 * The latest end a click may ask for. Midnight itself is out: the picker
 * would show it as "12:00 AM", which reads back as the start of the day and
 * no longer comes after the start.
 */
const LATEST_CLICK_END_MINUTES = MINUTES_PER_DAY - SNAP_MINUTES;

/**
 * The window a click on the bar asks for: the clicked minute snapped to the
 * half hour, keeping the current duration, and pulled back so the end stays
 * inside `window` and before midnight. With no window picked yet, an hour
 * from the click.
 */
export const moveSelectionTo = (
  clickedMinutes: number,
  window: MinuteRange,
  selection: MinuteRange | null
): MinuteRange => {
  const duration = selection
    ? selection.endMinutes - selection.startMinutes
    : DEFAULT_DURATION_MINUTES;
  const snapped = Math.round(clickedMinutes / SNAP_MINUTES) * SNAP_MINUTES;
  const latestEnd = Math.min(window.endMinutes, LATEST_CLICK_END_MINUTES);
  const latestStart = Math.max(window.startMinutes, latestEnd - duration);
  const startMinutes = Math.min(
    latestStart,
    Math.max(window.startMinutes, snapped)
  );
  return { startMinutes, endMinutes: startMinutes + duration };
};

/**
 * Dragging moves in the time picker's own steps, so whatever a drag lands on
 * is an entry the picker lists.
 */
const DRAG_SNAP_MINUTES = 15;
/** The latest end a drag may reach, for the same reason as a click's. */
const LATEST_DRAG_END_MINUTES = MINUTES_PER_DAY - DRAG_SNAP_MINUTES;

const snapToDragStep = (minutes: number): number =>
  Math.round(minutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;

/**
 * The window a drag of one of the handle's edges asks for: the dragged edge
 * follows the pointer in picker steps, the other one stays put. The edge
 * never crosses the other one — at least one step is always booked — and
 * never leaves `window`.
 */
export const resizeSelectionTo = (
  edge: "start" | "end",
  pointerMinutes: number,
  window: MinuteRange,
  selection: MinuteRange
): MinuteRange => {
  const snapped = snapToDragStep(pointerMinutes);
  if (edge === "start") {
    const startMinutes = Math.min(
      selection.endMinutes - DRAG_SNAP_MINUTES,
      Math.max(window.startMinutes, snapped)
    );
    return { startMinutes, endMinutes: selection.endMinutes };
  }
  const endMinutes = Math.max(
    selection.startMinutes + DRAG_SNAP_MINUTES,
    Math.min(window.endMinutes, LATEST_DRAG_END_MINUTES, snapped)
  );
  return { startMinutes: selection.startMinutes, endMinutes };
};

/**
 * The window a drag of the whole handle asks for: `startMinutes` is where
 * the pointer would put the start, in picker steps, keeping the duration and
 * pulled back so the window stays inside `window` and before midnight.
 */
export const dragSelectionTo = (
  startMinutes: number,
  window: MinuteRange,
  selection: MinuteRange
): MinuteRange => {
  const duration = selection.endMinutes - selection.startMinutes;
  const latestEnd = Math.min(window.endMinutes, LATEST_DRAG_END_MINUTES);
  const latestStart = Math.max(window.startMinutes, latestEnd - duration);
  const start = Math.min(
    latestStart,
    Math.max(window.startMinutes, snapToDragStep(startMinutes))
  );
  return { startMinutes: start, endMinutes: start + duration };
};

const rangesOverlap = (left: MinuteRange, right: MinuteRange): boolean =>
  left.startMinutes < right.endMinutes && right.startMinutes < left.endMinutes;

const isTitleWithheld = (schedule: ISchedule): boolean =>
  schedule.is_obfuscated === true ||
  schedule.visibility === "private" ||
  schedule.visibility === "confidential" ||
  schedule.user?.id === PRIVATE_USER_ID;

/**
 * The bookings that touch `day` (`yyyy-MM-dd`), clipped to it.
 *
 * A booking that starts the day before or ends the day after is shown from
 * midnight to midnight: what the requester needs to know is that the resource is
 * taken, not when the other booking began.
 */
export const scheduleBusyBlocks = (
  schedules: ISchedule[],
  day: string
): ResourceBusyBlock[] =>
  schedules
    .filter((schedule) => {
      if (!schedule.start || !schedule.end) return false;
      const startDay = getDateFromSchedule(schedule.start);
      const endDay = getDateFromSchedule(schedule.end);
      return startDay <= day && day <= endDay;
    })
    .map((schedule) => {
      const startsBefore = getDateFromSchedule(schedule.start) < day;
      const endsAfter = getDateFromSchedule(schedule.end) > day;
      const startMinutes = startsBefore
        ? 0
        : (timeToMinutes(getTimeFromSchedule(schedule.start, "hh:mm a")) ?? 0);
      const endMinutes = endsAfter
        ? MINUTES_PER_DAY
        : (timeToMinutes(getTimeFromSchedule(schedule.end, "hh:mm a")) ??
          MINUTES_PER_DAY);
      return {
        id: schedule.id,
        startMinutes,
        endMinutes,
        title: isTitleWithheld(schedule) ? null : schedule.title || null
      };
    })
    .filter((block) => block.endMinutes > block.startMinutes)
    .sort((left, right) => left.startMinutes - right.startMinutes);

/**
 * The window the requester is asking for, or `null` while it is not one yet:
 * a time missing, or an end that does not come after the start.
 */
export const selectionRange = (times: {
  startTime: string;
  endTime: string;
}): MinuteRange | null => {
  const startMinutes = timeToMinutes(times.startTime);
  const endMinutes = timeToMinutes(times.endTime);
  if (startMinutes === null || endMinutes === null) return null;
  if (endMinutes <= startMinutes) return null;
  return { startMinutes, endMinutes };
};

/**
 * The hours the bar spans: working hours, widened to whole hours only when
 * the requester picked a window outside them.
 *
 * Bookings do not widen it: a meeting running to half past midnight would
 * stretch the whole day into the bar and squeeze the hours that matter. A
 * booking outside the window is simply not shown — unless it clashes with
 * the request, in which case the request has already opened the window.
 */
export const resourceDayWindow = (
  selection: MinuteRange | null
): MinuteRange => {
  const ranges: MinuteRange[] = [
    DEFAULT_RESOURCE_DAY_WINDOW,
    ...(selection ? [selection] : [])
  ];
  const earliest = Math.min(...ranges.map((range) => range.startMinutes));
  const latest = Math.max(...ranges.map((range) => range.endMinutes));
  return {
    startMinutes: Math.max(
      0,
      Math.floor(earliest / MINUTES_PER_HOUR) * MINUTES_PER_HOUR
    ),
    endMinutes: Math.min(
      MINUTES_PER_DAY,
      Math.ceil(latest / MINUTES_PER_HOUR) * MINUTES_PER_HOUR
    )
  };
};

/** The blocks with any part inside `window`: what the bar draws and counts. */
export const blocksWithin = (
  blocks: ResourceBusyBlock[],
  window: MinuteRange
): ResourceBusyBlock[] =>
  blocks.filter((block) => rangesOverlap(block, window));

export const conflictingBlocks = (
  blocks: ResourceBusyBlock[],
  selection: MinuteRange
): ResourceBusyBlock[] =>
  blocks.filter((block) => rangesOverlap(block, selection));

/** Minutes of `window` covered by at least one block; overlaps count once. */
export const busyMinutesWithin = (
  blocks: ResourceBusyBlock[],
  window: MinuteRange
): number => {
  let covered = 0;
  let cursor = window.startMinutes;
  for (const block of [...blocks].sort(
    (left, right) => left.startMinutes - right.startMinutes
  )) {
    const start = Math.max(block.startMinutes, cursor);
    const end = Math.min(block.endMinutes, window.endMinutes);
    if (end <= start) continue;
    covered += end - start;
    cursor = end;
  }
  return covered;
};

/** "5 booked · 7h free", the bar's right-hand caption. */
export const resourceDaySummary = (
  blocks: ResourceBusyBlock[],
  window: MinuteRange
): string => {
  const freeMinutes =
    window.endMinutes - window.startMinutes - busyMinutesWithin(blocks, window);
  const freeHours = Math.round(freeMinutes / MINUTES_PER_HOUR);
  return `${blocks.length} booked · ${freeHours}h free`;
};

export type ResourceAvailabilityStatus = {
  tone: "free" | "conflict";
  text: string;
};

/** One requested day and what is already booked on it. */
export type ResourceDayBlocks = {
  day: Date;
  blocks: ResourceBusyBlock[];
};

const timeRangeLabel = (range: MinuteRange): string =>
  `${minutesToTimeLabel(range.startMinutes)} – ${minutesToTimeLabel(range.endMinutes)}`;

/** "Sep 18, Sep 20 and Sep 21" */
const dayListLabel = (days: Date[]): string => {
  const labels = days.map((day) => format(day, "MMM d"));
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

/**
 * The verdict for a request spanning several days, which share one window.
 * Free only when every day is free; otherwise it names the days in the way,
 * and the one booking when there is exactly one.
 */
export const daysAvailabilityStatus = (params: {
  resourceName: string;
  days: ResourceDayBlocks[];
  selection: MinuteRange | null;
}): ResourceAvailabilityStatus | null => {
  const { resourceName, days, selection } = params;
  if (!selection || days.length === 0) return null;
  if (days.length === 1) {
    return resourceAvailabilityStatus({
      resourceName,
      day: days[0].day,
      blocks: days[0].blocks,
      selection
    });
  }
  const busyDays = days
    .map((entry) => ({
      day: entry.day,
      conflicts: conflictingBlocks(entry.blocks, selection)
    }))
    .filter((entry) => entry.conflicts.length > 0);
  if (busyDays.length === 0) {
    return {
      tone: "free",
      text: `${resourceName} is free ${timeRangeLabel(selection)} on all ${days.length} days.`
    };
  }
  if (busyDays.length === 1 && busyDays[0].conflicts.length === 1) {
    const [conflict] = busyDays[0].conflicts;
    const holder = conflict.title ?? "Another booking";
    return {
      tone: "conflict",
      text: `${holder} already holds ${resourceName} ${timeRangeLabel(conflict)} on ${format(busyDays[0].day, "MMM d")}.`
    };
  }
  return {
    tone: "conflict",
    text: `Busy on ${dayListLabel(busyDays.map((entry) => entry.day))}.`
  };
};

/**
 * The verdict under the bar. `null` until the requester has picked a window,
 * because there is nothing to check before then.
 */
export const resourceAvailabilityStatus = (params: {
  resourceName: string;
  day: Date;
  blocks: ResourceBusyBlock[];
  selection: MinuteRange | null;
}): ResourceAvailabilityStatus | null => {
  const { resourceName, day, blocks, selection } = params;
  if (!selection) return null;
  const conflicts = conflictingBlocks(blocks, selection);
  if (conflicts.length === 0) {
    return {
      tone: "free",
      text: `${resourceName} is free ${minutesToTimeLabel(selection.startMinutes)} – ${minutesToTimeLabel(selection.endMinutes)} on ${format(day, "MMM d")}.`
    };
  }
  if (conflicts.length === 1) {
    const [conflict] = conflicts;
    const holder = conflict.title ?? "Another booking";
    return {
      tone: "conflict",
      text: `${holder} already holds ${resourceName} ${minutesToTimeLabel(conflict.startMinutes)} – ${minutesToTimeLabel(conflict.endMinutes)}.`
    };
  }
  return {
    tone: "conflict",
    text: `${conflicts.length} existing bookings overlap this window.`
  };
};
